<script setup>
import { ref, onMounted } from 'vue'
import { setLocales, i18n, initialLocale } from '../lang'
import zh from '@/assets/icon/zh.png'
import tw from '@/assets/icon/tw.png'
import en from '@/assets/icon/en.png'

const { locale, t } = i18n.global
const show = ref(false)
const radio = ref(initialLocale)

const langOptions = [
  { value: 'zh', label: '简体中文', icon: zh },
  { value: 'tw', label: '繁體中文', icon: tw },
  { value: 'en', label: 'English', icon: en }
]

const open = () => {
  show.value = true
}

onMounted(() => {
  radio.value = initialLocale
})

const onLangSelect = (lang) => {
  const selectedLocale = setLocales(lang)
  radio.value = selectedLocale
  locale.value = selectedLocale
  show.value = false
}

defineExpose({ open })
</script>

<template>
  <van-popup
    position="bottom"
    :show="show"
    round
    :style="{ background: 'transparent' }"
    @close="show = false">
    <div class="lang-sheet">
      <div class="sheet-handle"></div>
      <h3 class="sheet-title">{{ t('langList.selectLanguage') }}</h3>
      <div class="lang-list">
        <button
          v-for="option in langOptions"
          :key="option.value"
          class="lang-item"
          :class="{ active: radio === option.value }"
          @click="onLangSelect(option.value)">
          <img :src="option.icon" :alt="option.label" class="lang-flag" />
          <span class="lang-name">{{ option.label }}</span>
          <span v-if="radio === option.value" class="check-mark">
            <van-icon name="success" />
          </span>
          <span v-else class="check-circle"></span>
        </button>
      </div>
    </div>
  </van-popup>
</template>

<style scoped lang="scss">
.lang-sheet {
  padding: 1.25rem 1rem calc(2rem + env(safe-area-inset-bottom));
  background: #111419;
  border-radius: 1.25rem 1.25rem 0 0;
  border-top: 1px solid rgba(243, 186, 47, .22);
}

.sheet-handle {
  width: 2.5rem;
  height: .28rem;
  margin: 0 auto .9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, .2);
}

.sheet-title {
  margin: 0 0 1rem;
  padding-bottom: .85rem;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
  color: #f7f8fa;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.lang-list {
  display: grid;
  gap: .5rem;
}

.lang-item {
  display: flex;
  align-items: center;
  gap: .85rem;
  width: 100%;
  min-height: 3.25rem;
  padding: 0 .85rem;
  border: 1px solid rgba(255, 255, 255, .07);
  border-radius: .75rem;
  color: #d5dae1;
  background: rgba(255, 255, 255, .04);
  font: inherit;
  font-size: .88rem;
  cursor: pointer;
  text-align: left;
  touch-action: manipulation;
  transition: background .15s, border-color .15s;

  &.active {
    border-color: rgba(243, 186, 47, .5);
    background: rgba(243, 186, 47, .08);
    color: #f3ba2f;
  }

  &:not(.active):active {
    background: rgba(255, 255, 255, .07);
  }
}

.lang-flag {
  width: 1.85rem;
  height: 1.85rem;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, .12);
}

.lang-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.check-mark {
  flex: 0 0 1.5rem;
  display: grid;
  place-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  color: #101215;
  background: #f3ba2f;
  font-size: .8rem;
}

.check-circle {
  flex: 0 0 1.5rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, .2);
}

@media (prefers-reduced-motion: reduce) {
  .lang-item {
    transition: none !important;
  }
}
</style>
